import React, { useState } from 'react';
import axios from 'axios';
import { 
  Droplets, Home, Leaf, PaintBucket, Wrench, 
  Trash2, Settings, Award, Users, DollarSign, 
  CheckCircle, Phone, Mail, MapPin, Facebook,
  Star, ArrowRight, Menu, X, Layers
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { companyInfo, services, whyChooseUs, testimonials } from '../mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Layers, Droplets, Home, Leaf, PaintBucket, Wrench, Trash2, Settings,
  Award, Users, DollarSign, CheckCircle
};

const Landing = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Call backend API
      const response = await axios.post(`${API}/inquiries`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message
      });
      
      if (response.status === 201) {
        toast.success("Thank you! We'll contact you within 24 hours for your free evaluation.");
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("Sorry, there was an error submitting your request. Please call us directly at " + companyInfo.phone);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_bf199d59-89ca-4c2f-81ce-abb00116c3d3/artifacts/zqiz56kg_GMI%20Logo.jpg" 
                alt="Grey Matter Innovations Logo" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-slate-800">{companyInfo.name}</h1>
                <p className="text-xs text-slate-600">Serving Central Ohio</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => scrollToSection('services')} className="text-slate-700 hover:text-teal-600 transition-colors">
                Services
              </button>
              <button onClick={() => scrollToSection('about')} className="text-slate-700 hover:text-teal-600 transition-colors">
                About
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="text-slate-700 hover:text-teal-600 transition-colors">
                Testimonials
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-slate-700 hover:text-teal-600 transition-colors">
                Contact
              </button>
              <Button onClick={() => scrollToSection('contact')} className="bg-teal-600 hover:bg-teal-700">
                Free Evaluation
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-slate-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              <button onClick={() => scrollToSection('services')} className="block w-full text-left py-2 text-slate-700 hover:text-teal-600">
                Services
              </button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 text-slate-700 hover:text-teal-600">
                About
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="block w-full text-left py-2 text-slate-700 hover:text-teal-600">
                Testimonials
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2 text-slate-700 hover:text-teal-600">
                Contact
              </button>
              <Button onClick={() => scrollToSection('contact')} className="w-full bg-teal-600 hover:bg-teal-700">
                Free Evaluation
              </Button>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-slate-50 via-teal-50/30 to-slate-50">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-5xl md:text-6xl font-bold text-slate-800 leading-tight">
                Your Trusted Partner for Home Solutions
              </h2>
              <p className="text-xl text-slate-600 leading-relaxed">
                Cost-effective maintenance and restoration services for Central Ohio homeowners. 
                Over 10 years of expert craftsmanship you can count on.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => scrollToSection('contact')} 
                  size="lg" 
                  className="bg-teal-600 hover:bg-teal-700 text-lg px-8 py-6"
                >
                  Get Free Evaluation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 text-lg px-8 py-6"
                  onClick={() => window.location.href = `tel:${companyInfo.phone}`}
                >
                  <Phone className="mr-2" size={20} />
                  Call Now
                </Button>
              </div>
              <div className="flex items-center space-x-6 pt-4">
                <div className="flex items-center space-x-2 text-slate-700">
                  <Phone size={20} className="text-teal-600" />
                  <span className="font-semibold">{companyInfo.phone}</span>
                </div>
                <div className="flex items-center space-x-2 text-slate-700">
                  <MapPin size={20} className="text-teal-600" />
                  <span>{companyInfo.serviceArea}</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/544965/pexels-photo-544965.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Professional Construction Services"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
                <p className="text-3xl font-bold text-teal-600">10+</p>
                <p className="text-slate-600 font-medium">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">Our Services</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Comprehensive home maintenance and restoration solutions tailored to your needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-2 hover:border-teal-500 hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="text-teal-600" size={28} />
                    </div>
                    <CardTitle className="text-xl text-slate-800">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-slate-600 text-base leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="about" className="py-20 px-4 bg-slate-50">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1505798577917-a65157d3320a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxob21lJTIwbWFpbnRlbmFuY2V8ZW58MHx8fHwxNzcxODAwMDg0fDA&ixlib=rb-4.1.0&q=85"
                alt="Expert Craftsmanship"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                  Why Choose Grey Matter Innovations?
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Led by Travis Conley, we bring over a decade of experience in residential construction 
                  and exterior restoration to every project. Our commitment to quality, fair pricing, 
                  and community values sets us apart.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-6">
                {whyChooseUs.map((item) => {
                  const Icon = iconMap[item.icon];
                  return (
                    <div key={item.id} className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="text-teal-600" size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">{item.title}</h3>
                        <p className="text-slate-600">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">What Our Neighbors Say</h2>
            <p className="text-xl text-slate-600">Trusted by homeowners across Central Ohio</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2">
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="text-amber-400 fill-amber-400" size={20} />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 italic leading-relaxed">"{testimonial.text}"</p>
                  <div>
                    <p className="font-bold text-slate-800">{testimonial.name}</p>
                    <p className="text-sm text-slate-600">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-teal-600">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
            Contact us today for your free on-site evaluation and pricing. No obligation, just honest advice.
          </p>
          <Button 
            onClick={() => scrollToSection('contact')} 
            size="lg" 
            className="bg-white text-teal-600 hover:bg-slate-100 text-lg px-8 py-6"
          >
            Schedule Free Evaluation
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-slate-50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">Get In Touch</h2>
                <p className="text-lg text-slate-600">
                  Have a project in mind? Reach out for a free evaluation and let's discuss how we can help.
                </p>
              </div>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 mb-1">Phone</p>
                    <a href={`tel:${companyInfo.phone}`} className="text-teal-600 hover:text-teal-700 text-lg">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 mb-1">Email</p>
                    <a href={`mailto:${companyInfo.email}`} className="text-teal-600 hover:text-teal-700 break-all">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 mb-1">Service Area</p>
                    <p className="text-slate-600">{companyInfo.serviceArea}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Facebook className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 mb-1">Follow Us</p>
                    <a 
                      href={companyInfo.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-teal-600 hover:text-teal-700"
                    >
                      Facebook
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-2xl">Request Free Evaluation</CardTitle>
                <CardDescription>Fill out the form and we'll get back to you within 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Input
                      type="text"
                      name="service"
                      placeholder="Service Needed (e.g., Power Washing)"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Textarea
                      name="message"
                      placeholder="Tell us about your project..."
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-lg py-6">
                    Submit Request
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bf199d59-89ca-4c2f-81ce-abb00116c3d3/artifacts/zqiz56kg_GMI%20Logo.jpg" 
                  alt="Grey Matter Innovations Logo" 
                  className="h-10 w-10 object-contain"
                />
                <h3 className="text-xl font-bold">{companyInfo.name}</h3>
              </div>
              <p className="text-slate-400">
                Your trusted partner for cost-effective home solutions and maintenance in Central Ohio.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-slate-400 hover:text-teal-400 transition-colors">
                    Services
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-slate-400 hover:text-teal-400 transition-colors">
                    About Us
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('testimonials')} className="text-slate-400 hover:text-teal-400 transition-colors">
                    Testimonials
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-slate-400 hover:text-teal-400 transition-colors">
                    Contact
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-slate-400">
                <li>{companyInfo.phone}</li>
                <li>{companyInfo.email}</li>
                <li>{companyInfo.serviceArea}</li>
                <li>
                  <a 
                    href={companyInfo.facebook} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-teal-400 transition-colors inline-flex items-center space-x-2"
                  >
                    <Facebook size={18} />
                    <span>Follow us on Facebook</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-700 pt-8 text-center text-slate-400">
            <p>&copy; {new Date().getFullYear()} {companyInfo.name}. All rights reserved.</p>
            <p className="mt-2">Serving the Central Ohio community with pride.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
